import React from 'react'
import PropTypes from 'prop-types'
import { FileText, Download, ExternalLink, RefreshCw } from 'lucide-react'
import './ReportCard.css'

/**
 * ReportCard Component
 *
 * A card component for displaying report sections with title, description,
 * optional actions, and content area.
 *
 * @param {Object} props
 * @param {string} props.title - Report section title
 * @param {string} props.description - Optional description text
 * @param {React.ReactNode} props.children - Content to render inside the card
 * @param {React.ReactNode} props.headerActions - Actions to display in header
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.collapsible - Whether card can be collapsed
 * @param {boolean} props.defaultCollapsed - Initial collapsed state
 * @param {Function} props.onRefresh - Refresh handler (shows refresh button if provided)
 * @param {Function} props.onExport - Export handler (shows export button if provided)
 * @param {string} props.className - Additional CSS classes
 */
const ReportCard = ({
	title,
	description,
	children,
	headerActions,
	loading = false,
	collapsible = false,
	defaultCollapsed = false,
	onRefresh,
	onExport,
	className = '',
}) => {
	const [isCollapsed, setIsCollapsed] = React.useState(defaultCollapsed)

	const handleToggleCollapse = () => {
		if (collapsible) {
			setIsCollapsed(!isCollapsed)
		}
	}

	const cardClasses = ['report-card', className, loading ? 'report-card--loading' : '', isCollapsed ? 'report-card--collapsed' : '']
		.filter(Boolean)
		.join(' ')

	return (
		<div className={cardClasses}>
			<div className="report-card__header" onClick={collapsible ? handleToggleCollapse : undefined} role={collapsible ? 'button' : undefined}>
				<div className="report-card__header-content">
					<div className="report-card__icon">
						<FileText size={20} />
					</div>
					<div className="report-card__header-text">
						<h3 className="report-card__title">{title}</h3>
						{description && <p className="report-card__description">{description}</p>}
					</div>
				</div>

				<div className="report-card__header-actions">
					{headerActions}

					{onRefresh && (
						<button className="report-card__action-btn" onClick={(e) => { e.stopPropagation(); onRefresh(); }} aria-label="Refresh">
							<RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
						</button>
					)}

					{onExport && (
						<button className="report-card__action-btn" onClick={(e) => { e.stopPropagation(); onExport(); }} aria-label="Export">
							<Download size={16} />
						</button>
					)}

					{collapsible && (
						<button className="report-card__collapse-btn" aria-label={isCollapsed ? 'Expand' : 'Collapse'}>
							<ExternalLink size={16} style={{ transform: isCollapsed ? 'rotate(180deg)' : 'none' }} />
						</button>
					)}
				</div>
			</div>

			{!isCollapsed && (
				<div className="report-card__body">
					{loading ? (
						<div className="report-card__loading">
							<div className="report-card__spinner"></div>
							<span>Loading report data...</span>
						</div>
					) : (
						children
					)}
				</div>
			)}
		</div>
	)
}

ReportCard.propTypes = {
	title: PropTypes.string.isRequired,
	description: PropTypes.string,
	children: PropTypes.node,
	headerActions: PropTypes.node,
	loading: PropTypes.bool,
	collapsible: PropTypes.bool,
	defaultCollapsed: PropTypes.bool,
	onRefresh: PropTypes.func,
	onExport: PropTypes.func,
	className: PropTypes.string,
}

export default ReportCard
